import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faStar,
  faSpinner,
  faShieldAlt,
  faRocket,
  faLightbulb,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { faGoogle } from '@fortawesome/free-brands-svg-icons';
import { useAuth } from '../contexts/AuthContext';

const AuthPage: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser, login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if already authenticated
  if (currentUser) {
    return <Navigate to="/" replace />;
  }

  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);
      setError(null);
      await login();
    } catch (error: any) {
      console.error('Login failed:', error);
      setError(error.message || 'Giriş yaparken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setIsLoading(false);
    }
  };

  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #f5f3ff 0%, #e9d5ff 30%, #c7d2fe 60%, #e9d5ff 100%)'
  };

  const backgroundPatternStyle: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23a855f7' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    opacity: 0.3
  };

  const floatingElementBase: React.CSSProperties = {
    position: 'absolute',
    borderRadius: '50%',
    filter: 'blur(40px)',
    animationName: 'float',
    animationDuration: '6s',
    animationTimingFunction: 'ease-in-out',
    animationIterationCount: 'infinite'
  };

  const containerStyle: React.CSSProperties = {
    position: 'relative',
    zIndex: 10,
    maxWidth: '480px',
    width: '100%',
    margin: '0 1rem'
  };

  const cardStyle: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    borderRadius: '24px',
    padding: '3rem 2rem',
    textAlign: 'center',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    border: '1px solid rgba(255, 255, 255, 0.3)'
  };

  const logoContainerStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '80px',
    height: '80px',
    background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
    borderRadius: '50%',
    marginBottom: '1.5rem',
    boxShadow: '0 10px 25px rgba(139, 92, 246, 0.3)'
  };

  const buttonStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.75rem',
    background: 'white',
    border: '2px solid #e5e7eb',
    color: '#374151',
    fontWeight: '600',
    fontSize: '1rem',
    padding: '1rem 1.5rem',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden'
  };

  const buttonHoverStyle = {
    border: '2px solid #8b5cf6',
    color: '#8b5cf6',
    transform: 'translateY(-2px)',
    boxShadow: '0 10px 25px rgba(139, 92, 246, 0.2)'
  };

  const featureStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '1rem'
  };

  const featureIconStyle: React.CSSProperties = {
    width: '48px',
    height: '48px',
    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1), rgba(124, 58, 237, 0.1))',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 0.75rem',
    border: '2px solid rgba(139, 92, 246, 0.2)'
  };

  return (
    <div style={pageStyle}>
      {/* Background Pattern */}
      <div style={backgroundPatternStyle}></div>

      {/* Floating Elements */}
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{
          ...floatingElementBase,
          top: '10%',
          left: '10%',
          width: '80px',
          height: '80px',
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(168, 85, 247, 0.3))'
        }}></div>
        <div style={{
          ...floatingElementBase,
          top: '20%',
          right: '15%',
          width: '120px',
          height: '120px',
          background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.3), rgba(139, 92, 246, 0.3))',
          animationDelay: '2s'
        }}></div>
        <div style={{
          ...floatingElementBase,
          bottom: '20%',
          left: '15%',
          width: '100px',
          height: '100px',
          background: 'linear-gradient(135deg, rgba(124, 58, 237, 0.3), rgba(139, 92, 246, 0.3))',
          animationDelay: '4s'
        }}></div>
        <div style={{
          ...floatingElementBase,
          bottom: '10%',
          right: '10%',
          width: '60px',
          height: '60px',
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3), rgba(124, 58, 237, 0.3))',
          animationDelay: '1s'
        }}></div>
      </div>

      <div style={containerStyle}>
        <div style={cardStyle}>
          {/* Logo Section */}
          <div style={logoContainerStyle}>
            <img 
              src="/logo.svg" 
              alt="Kairoscope" 
              style={{ height: '40px', width: 'auto' }}
            />
          </div>

          {/* Title */}
          <h1 style={{ 
            fontSize: '2rem', 
            fontWeight: 'bold', 
            color: '#1f2937', 
            marginBottom: '0.5rem' 
          }}>
            Kairoscope'a Hoş Geldiniz
          </h1>
          
          <p style={{ 
            color: '#6b7280', 
            fontSize: '1.125rem', 
            lineHeight: 1.6,
            marginBottom: '2rem' 
          }}>
            AI destekli kişiselleştirilmiş numeroloji analizlerinize erişmek için giriş yapın.
          </p>

          {/* Error Message */}
          {error && (
            <div style={{
              marginBottom: '1.5rem',
              padding: '1rem',
              background: 'linear-gradient(135deg, #fef2f2, #fee2e2)',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              color: '#dc2626'
            }}>
              <FontAwesomeIcon icon={faCheck} style={{ marginRight: '0.5rem' }} />
              {error}
            </div>
          )}

          {/* Google Login Button */}
          <button
            onClick={handleGoogleLogin}
            disabled={isLoading}
            style={{
              ...buttonStyle,
              ...(isLoading ? { opacity: 0.7, cursor: 'not-allowed' } : {})
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                Object.assign(e.currentTarget.style, buttonHoverStyle);
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                Object.assign(e.currentTarget.style, buttonStyle);
              }
            }}
          >
            {isLoading ? (
              <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '1.25rem' }} />
            ) : (
              <FontAwesomeIcon icon={faGoogle} style={{ fontSize: '1.25rem', color: '#ea4335' }} />
            )}
            <span>
              {isLoading ? 'Giriş Yapılıyor...' : 'Google ile Giriş Yap'}
            </span>
          </button>

          {/* Terms */}
          <div style={{ 
            marginTop: '1.5rem', 
            fontSize: '0.875rem', 
            color: '#6b7280',
            lineHeight: 1.5
          }}>
            <p>
              Giriş yaparak{' '}
              <a 
                href="/terms" 
                style={{ color: '#8b5cf6', textDecoration: 'underline' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#7c3aed'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#8b5cf6'}
              >
                Kullanım Şartları
              </a>{' '}
              ve{' '}
              <a 
                href="/privacy" 
                style={{ color: '#8b5cf6', textDecoration: 'underline' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#7c3aed'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#8b5cf6'}
              >
                Gizlilik Politikası
              </a>
              'nı kabul etmiş olursunuz.
            </p>
          </div>
        </div>

        {/* Features */}
        <div style={{ 
          marginTop: '2rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1rem'
        }}>
          <div style={featureStyle}>
            <div style={featureIconStyle}>
              <FontAwesomeIcon icon={faShieldAlt} style={{ color: '#8b5cf6', fontSize: '1.25rem' }} />
            </div>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>
              Güvenli ve<br />Gizli
            </p>
          </div>
          
          <div style={featureStyle}>
            <div style={featureIconStyle}>
              <FontAwesomeIcon icon={faRocket} style={{ color: '#8b5cf6', fontSize: '1.25rem' }} />
            </div>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>
              Hızlı<br />Analiz
            </p>
          </div>
          
          <div style={featureStyle}>
            <div style={featureIconStyle}>
              <FontAwesomeIcon icon={faLightbulb} style={{ color: '#8b5cf6', fontSize: '1.25rem' }} />
            </div>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', fontWeight: '500' }}>
              Kişiselleştirilmiş<br />İçgörüler
            </p>
          </div>
        </div>

        {/* Additional Info */}
        <div style={{
          marginTop: '2rem',
          textAlign: 'center',
          padding: '1.5rem',
          background: 'rgba(255, 255, 255, 0.6)',
          borderRadius: '16px',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.3)'
        }}>
          <h3 style={{ 
            color: '#374151', 
            fontSize: '1.125rem', 
            fontWeight: '600',
            marginBottom: '0.75rem' 
          }}>
            Neden Kairoscope?
          </h3>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(2, 1fr)', 
            gap: '0.75rem',
            textAlign: 'left'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FontAwesomeIcon icon={faCheck} style={{ color: '#10b981', fontSize: '0.875rem' }} />
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>AI destekli analiz</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FontAwesomeIcon icon={faCheck} style={{ color: '#10b981', fontSize: '0.875rem' }} />
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Detaylı raporlar</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FontAwesomeIcon icon={faCheck} style={{ color: '#10b981', fontSize: '0.875rem' }} />
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>Çoklu analiz türü</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <FontAwesomeIcon icon={faCheck} style={{ color: '#10b981', fontSize: '0.875rem' }} />
              <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>PDF indirme</span>
            </div>
          </div>
        </div>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          33% { transform: translateY(-20px) rotate(120deg); }
          66% { transform: translateY(10px) rotate(240deg); }
        }
      `}</style>
    </div>
  );
};

export default AuthPage; 